import { useQuery } from 'react-query';
import axios from 'axios';

import { backendRequestHeaders, getBackendBaseUrl, resolveBearerToken } from '@/api/backendAuth';
import { normalizeAudienceMemberDetail } from '@/api/workspaces/normalizeAudienceMemberDetail';
import { useAppSelector } from '@/app/hooks';
import { MemberOverview } from '@/models/AudienceByWorkspace';
import { useAuth } from '@/modules/Auth/hooks/useAuth';
import { selectCurrentWorkspace } from '@/modules/Dashboard/Workspaces/workspaceSlice';

import queryKeys from '../../modules/shared/query/queryKeys';

async function getAudienceMemberById(
  sessionJwt: string | undefined,
  workspaceId: string,
  audienceMemberId: string,
): Promise<MemberOverview> {
  const base = getBackendBaseUrl();
  const headers = backendRequestHeaders(sessionJwt);
  if (!headers.Authorization) {
    throw new Error('Missing Bearer token for audience member request.');
  }

  const { data } = await axios.get<unknown>(`${base}/workspaces/${workspaceId}/audience/${audienceMemberId}`, {
    headers,
  });

  return normalizeAudienceMemberDetail(data);
}

export function useGetAudienceMemberById(audienceMemberId: string | undefined) {
  const { user } = useAuth();
  const workspace = useAppSelector(selectCurrentWorkspace);

  const extractWorkspaceId = (workspaceObj: any) => {
    if (!workspaceObj) return '';
    if (workspaceObj.id) return workspaceObj.id;
    if (workspaceObj.SK) {
      if (workspaceObj.SK.startsWith('WS#')) {
        return workspaceObj.SK.substring(3);
      }
      return workspaceObj.SK;
    }
    return '';
  };

  const workspaceId = extractWorkspaceId(workspace);
  const sessionJwt = user?.signInUserSession?.idToken?.jwtToken;
  const bearer = resolveBearerToken(sessionJwt);

  return useQuery<MemberOverview>({
    queryKey: [queryKeys.users, workspaceId, audienceMemberId, 'detail'],
    queryFn: () => getAudienceMemberById(sessionJwt, workspaceId, audienceMemberId ?? ''),
    enabled: Boolean(audienceMemberId && workspaceId && user && bearer),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}
